import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Switch,
} from "@chakra-ui/react";
import { Formik } from "formik";
import { useState } from "react";
import {
  Column,
  EnviromentTranslation,
  TabularForecastTranslation,
} from "../types";

interface ManageTabularForecastColumnsModalProps {
  isOpen: boolean;
  onClose: () => void;
  columns: Column[];
  hidedColumns: string[];
  hidedSubColumns: string[];
  translate: EnviromentTranslation;
  translateForecast: TabularForecastTranslation;
  onUpdateHidedColumns: (data: string[]) => void;
  onUpdateSubHidedColumns: (data: string[]) => void;
}

interface FormItemData {
  columns: {
    [key: string]: boolean;
  };
  inputs: {
    [key: string]: boolean;
  };
}

export function ManageTabularForecastColumnsModal({
  isOpen,
  onClose,
  columns,
  hidedColumns,
  hidedSubColumns,
  translate,
  translateForecast,
  onUpdateHidedColumns,
  onUpdateSubHidedColumns,
}: ManageTabularForecastColumnsModalProps) {
  const [index, setIndex] = useState<number[]>([]);

  const initialData = columns.reduce(
    (acc: FormItemData, column) => {
      if (column && column.type !== "date") {
        acc.columns[column.type!] = !hidedColumns.includes(column.type!);

        if (column.inputs) {
          column.inputs.forEach((input) => {
            acc.inputs[input.label] = !hidedSubColumns.includes(input.label);
          });
        }
      }

      return acc;
    },
    {
      columns: {},
      inputs: {},
    }
  );

  const handleSubmit = (values: FormItemData) => {
    const columnsData = Object.entries(values.columns).map(([key, value]) => {
      if (!value) return key;

      return null;
    });

    const inputsData = Object.entries(values.inputs)
      .map(([key, value]) => {
        if (!value) return key;

        return null;
      })
      .filter((item) => item !== null) as string[];

    onUpdateHidedColumns(
      columnsData.filter((item) => item && item !== "date") as string[]
    );
    onUpdateSubHidedColumns(inputsData);
    onClose();
  };

  const isSubmitDisabled = (values: FormItemData) => {
    return Object.values(values).every((value) => !value);
  };

  const verifyIfColumnIsActive = (column: Column, values: FormItemData) => {
    let isActive = false;

    column.inputs?.forEach((input) => {
      if (values.inputs[input.label]) {
        isActive = true;
      }
    });

    return isActive;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="5xl">
      <ModalOverlay />
      <Formik
        initialValues={initialData}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue }) => {
          const activeIndex = Object.values(values.columns).reduce(
            (acc, item, index) => {
              if (!!item) {
                acc.push(index);
              }
              return acc;
            },
            [] as number[]
          );

          return (
            <ModalContent>
              <ModalHeader color="#006699">
                <h1>{translateForecast("manageColumnTitle")}</h1>
              </ModalHeader>
              <ModalCloseButton color="#006699" />

              <ModalBody color="black">
                <Accordion
                  allowToggle
                  defaultIndex={activeIndex}
                  allowMultiple
                  index={index.length === 0 ? undefined : index}
                  onChange={(index) => {
                    setIndex(index as number[]);
                  }}
                >
                  <Grid templateColumns="repeat(3, 1fr)" gap={6}>
                    {columns.map((column, indexKey) => {
                      if (!column || column.type === "date") {
                        return false;
                      }

                      return (
                        <AccordionItem
                          key={column.type}
                          borderWidth="1px"
                          height="auto"
                          borderRadius="4px"
                        >
                          <AccordionButton>
                            <FormControl
                              display="flex"
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <FormLabel htmlFor={column.type!} mb="0">
                                {translate(column.label!)}
                              </FormLabel>
                              <Switch
                                id={column.type!}
                                isChecked={values.columns[column.type!]}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setIndex((prevState) => {
                                      return [...prevState, indexKey - 1];
                                    });
                                  }

                                  setFieldValue(
                                    `columns.${column.type}`,
                                    e.target.checked
                                  );
                                }}
                              />
                            </FormControl>
                            <AccordionIcon />
                          </AccordionButton>
                          <AccordionPanel pb={4}>
                            <Flex
                              direction="column"
                              gap={2}
                              pl="10px"
                              pr="20px"
                            >
                              {column?.inputs?.map((input) => {
                                return (
                                  <FormControl
                                    key={input.key}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                  >
                                    <FormLabel
                                      htmlFor={input.label}
                                      mb="0"
                                      fontWeight="normal"
                                    >
                                      {translate(input.label)}
                                    </FormLabel>
                                    <Switch
                                      id={input.key}
                                      isChecked={values.inputs[input.label]}
                                      disabled={!values.columns[column.type!]}
                                      onChange={(e) => {
                                        setFieldValue(
                                          `inputs.${input.label}`,
                                          e.target.checked
                                        );

                                        if (!e.target.checked) {
                                          const copy = {
                                            ...values,
                                            inputs: {
                                              ...values.inputs,
                                              [input.label]: e.target.checked,
                                            },
                                          };

                                          const isActive =
                                            verifyIfColumnIsActive(
                                              column,
                                              copy
                                            );

                                          if (!isActive) {
                                            setFieldValue(
                                              `columns.${column.type}`,
                                              false
                                            );
                                          }
                                        }
                                      }}
                                    />
                                  </FormControl>
                                );
                              })}
                            </Flex>
                          </AccordionPanel>
                        </AccordionItem>
                      );
                    })}
                  </Grid>
                </Accordion>
              </ModalBody>

              <ModalFooter>
                <Button
                  key="back"
                  mr={3}
                  onClick={onClose}
                  color="newBlue.500"
                  variant="ghost"
                >
                  {translateForecast("manageColumnsModalCancel")}
                </Button>
                <Button
                  key="submit"
                  type="submit"
                  layerStyle="ocean"
                  _hover={{ layerStyle: "ocean" }}
                  isDisabled={isSubmitDisabled(values)}
                  onClick={() => handleSubmit(values)}
                >
                  {translateForecast("manageColumnsModalSave")}
                </Button>
              </ModalFooter>
            </ModalContent>
          );
        }}
      </Formik>
    </Modal>
  );
}
